import { InfoIcon } from '@phosphor-icons/react'
import invariant from 'tiny-invariant'
import type { ScanResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './resolution-panel-scan-info.css.ts'

export type ResolutionPanelScanInfoProps = {
  scan: Pick<ScanResponse, 'imported_at'>
}

/**
 * The scan metadata in the Resolution panel's head: when the scan was imported, and what it is.
 *
 * Two columns split by a rule, per the design, rather than the stacked rows the findings tab header
 * uses elsewhere — the panel gives this row the full width of the card, so the timestamp and the
 * description sit side by side instead of one above the other.
 */
export const ResolutionPanelScanInfo: React.FC<ResolutionPanelScanInfoProps> = ({ scan }) => {
  invariant(scan.imported_at, 'Scan is missing imported_at')

  return (
    <div className={styles.container}>
      <div className={styles.timeColumn}>
        <p className={styles.date}>{formatDate({ date: scan.imported_at, type: 'mm/dd/yy' })}</p>
        <p className={styles.time}>{formatDate({ date: scan.imported_at, type: 'time-with-zone' })}</p>
      </div>
      <div className={styles.metaColumn}>
        <p className={styles.meta}>Latest scan with analysis</p>
        <TooltipWithHover
          trigger={
            <p className={styles.metaSub}>
              Representative analysis per finding
              <InfoIcon className={styles.infoIcon} size={12} weight="light" />
            </p>
          }
          side="bottom"
          maxWidth={360}
        >
          <p className={styles.tooltipText}>
            For each finding with multiple analyses, Pixee will default to showing results from the most successful
            analysis as a top-level <strong>representative</strong>. If two analyses are equally successful, then the
            most recent of the two will be displayed.
          </p>
        </TooltipWithHover>
      </div>
    </div>
  )
}
